import React from "react";
import * as c from "./lpCompetitorSection.module.scss";
import classNames from "classnames";
import MangomintLogo from "@images/competitors/mangomint.svg";
import VagaroLogo from "@images/competitors/vagaro.svg";
import FreshaLogo from "@images/competitors/fresha.svg";
import ZenotiLogo from "@images/competitors/zenoti.svg";
import BoulevardLogo from "@images/competitors/boulevard.svg";
import MilanoLogo from "@images/competitors/milano.svg";
import AestheticRecordLogo from "@images/competitors/aestheticrecord.png";
import AestheticsProLogo from "@images/competitors/aestheticspro.png";
import BookerLogo from "@images/competitors/booker.png";
import MeevoLogo from "@images/competitors/meevo.svg";
import RosyLogo from "@images/competitors/rosy.png";
import PhorestLogo from "@images/competitors/phorest.png";
import StaticGradient from "@components/shared/staticGradient";
import { useScrolltriggerClassnames } from "@hooks/useScrolltriggerClassnames";

const pageData = {
  "switch-from-vagaro-now": {
    pageHeader: {
      heading: "Mangomint vs. Vagaro",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Vagaro",
      logo: VagaroLogo,
    },
    rows: [
      {
        col1: "Great for",
        col2: "For sophisticated businesses looking to grow",
        col3: "For basic operations with simple needs",
      },
      {
        col1: "Strengths",
        col2: "Premium design and usability. Optimized for speed and efficiency",
        col3: "Lots of features, but slow and lacking refinement",
      },
      {
        col1: "Support",
        col2: "U.S. based team with deep expertise. Fully staffed, responding in under 10 mins",
        col3: "Low-cost call center team, providing generic answers assisted by chatbots",
      },
      {
        col1: "Highlight",
        col2: "Increase profits by reducing costs and strengthening client retention with tools and automations that actually work",
        col3: "Cheap price aka 'you get what you paid for'",
      },
    ],
  },
  "switch-from-zenoti": {
    pageHeader: {
      heading: "Mangomint vs. Zenoti",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Zenoti",
      logo: ZenotiLogo,
    },
    rows: [
      {
        col1: "Great for",
        col2: "For growing, human-sized<sup>*</sup> salons and spas <cite><sup>*</sup>1-10 locations</i>",
        col3: "For enterprise-level salons and spas with many locations",
      },
      {
        col1: "Strengths",
        col2: "Intuitive, streamlined features that save you time running your business",
        col3: "Extensive feature set, but with overcomplicated workflows and steep learning curve",
      },
      {
        col1: "Support",
        col2: "U.S.-based team with deep expertise. Fully human staffed, responding in under 10 mins",
        col3: "Primarily ticket-based support (or non-human AI bot), with slower response times",
      },
      {
        col1: "Highlight",
        col2: "Solutions that work for bookings, payments, reporting, and client communications—without tech headaches",
        col3: "Extensive system for large businesses needing heavy customization (and can manage complexity)",
      },
    ],
  },
  "switch-from-fresha": {
    pageHeader: {
      heading: "Mangomint vs. Fresha",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Fresha",
      logo: FreshaLogo,
    },
    rows: [
      {
        col1: "Great for",
        col2: "For sophisticated businesses looking to grow",
        col3: "For small businesses looking for a minimal solution",
      },
      {
        col1: "Strengths",
        col2: "Premium design and usability. Optimized for speed and efficiency. No hidden fees",
        col3: "Subscription-free model with a large marketplace",
      },
      {
        col1: "Support",
        col2: "Live chat with U.S.-based team of salon & spa experts (real humans!). Responds in 10 mins or less",
        col3: "Limited support via email, operating only between 7 AM - 7 PM",
      },
      {
        col1: "Highlight",
        col2: "Advanced tools and automation flows to strengthen client retention, cut costs, and increase profits",
        col3: "Marketplace for acquiring new clients, but charges 20% commission on new client bookings",
      },
    ],
  },
  "switch-from-phorest": {
    pageHeader: {
      heading: "Mangomint vs. Phorest",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Phorest",
      logo: PhorestLogo,
    },
    rows: [
      {
        col1: "Great for",
        col2: "For growing businesses looking for a solution that balances functionality and ease",
        col3: "For businesses looking for a basic platform",
      },
      {
        col1: "Strengths",
        col2: "Flexible solutions for booking, reporting, marketing, and more—available across devices in a clean interface",
        col3: "Focus on loyalty and reporting features— but slow-to-update and limitations by device type",
      },
      {
        col1: "Support",
        col2: "U.S. based team with deep expertise. Fully staffed, responding in under 10 mins",
        col3: "Dedicated Salon Manager—but with wait times up to 72 hours",
      },
      {
        col1: "Highlight",
        col2: "Customizable, modern features from online booking to client communications—with no contracts",
        col3: "Basic core competencies and limited customizations for a “one and done” approach",
      },
    ],
  },
  "switch-from-boulevard": {
    pageHeader: {
      heading: "Mangomint vs. Boulevard",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Boulevard",
      logo: BoulevardLogo,
      height: "-5px",
    },
    rows: [
      {
        col1: "Great for",
        col2: "For sophisticated businesses looking to grow",
        col3: "For larger businesses seeking a marketing-focused tool",
      },
      {
        col1: "Strengths",
        col2: "Customizable features and beautiful interface with full functionality on desktop and mobile",
        col3: "Strong client management—but otherwise non-customizable features and significant mobile limitations",
      },
      {
        col1: "Support",
        col2: "U.S. based human team with deep expertise. Fully staffed, responding in under 10 mins",
        col3: "Chat bot-based service. Human-based support <i>if</i> requested (response times up to 1 day)",
      },
      {
        col1: "Highlight",
        col2: "Modern features from online booking to client communications—with no contracts",
        col3: "Solid feature set but only in structured, contract-based plans",
      },
    ],
  },
  "switch-from-milano": {
    pageHeader: {
      heading: "Mangomint vs. Milano",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Milano",
      logo: MilanoLogo,
      height: "12.5px",
    },
    rows: [
      {
        col1: "Great for",
        col2: "For sophisticated businesses looking to grow with a modern all-in-one solution",
        col3: "For businesses more comfortable with basic tools and familiar legacy software",
      },
      {
        col1: "Strengths",
        col2: "Premium design and usability. Complete solution from online booking to reporting to marketing",
        col3: "Legacy look and feel with basic booking and POS. Outdated flows and automations for marketing tools",
      },
      {
        col1: "Support",
        col2: "U.S. based team with deep expertise. Fully staffed, responding in under 10 mins",
        col3: "Monday to Saturday support team, with slower response times and less dedicated support",
      },
      {
        col1: "Highlight",
        col2: "A faster, smarter solution to run your salon or spa—with tools that simplify, support, and scale your business",
        col3: "Familiar legacy system that covers basic needs but struggles to keep up with modern demands",
      },
    ],
  },
  "switch-from-meevo": {
    pageHeader: {
      heading: "Mangomint vs. Meevo",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Meevo",
      logo: MeevoLogo,
      height: "-3.5px",
    },
    rows: [
      {
        col1: "Great for",
        col2: "For businesses that want premium software and features without the learning curve",
        col3: "For teams who want deep customization and don’t mind tech-heavy tools",
      },
      {
        col1: "Strengths",
        col2: "Clean interface, fast workflows, and zero fluff to get more done in less time",
        col3: "Powerful but bulky—expect a steep learning curve and lots of setup time",
      },
      {
        col1: "Support",
        col2: "Seasoned U.S.-based team of real humans with deep salon and spa knowledge (and fast replies!)",
        col3: "Mostly self-service with slow onboarding process",
      },
      {
        col1: "Highlight",
        col2: "Smart automations and sleek scheduling that don’t require an IT degree",
        col3: "Custom dashboards and widgets—but requires more time and tech know-how to manage",
      },
    ],
  },
  "switch-from-rosy": {
    pageHeader: {
      heading: "Mangomint vs. Rosy",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Rosy",
      logo: RosyLogo,
      height: "-2.5px",
    },
    rows: [
      {
        col1: "Great for",
        col2: "For growing salons & spas that need speed, style, and smart automation",
        col3: "For small salons looking for a basic, budget-friendly setup",
      },
      {
        col1: "Strengths",
        col2: "Polished, powerful, and designed to scale—without slowing you down",
        col3: "Simple and affordable—but outdated interface and limited advanced tools",
      },
      {
        col1: "Support",
        col2: "U.S.-based support team that actually understands salons and spas. Responds in under 10 mins",
        col3: "Slow and less tailored support available via chat or phone",
      },
      {
        col1: "Highlight",
        col2: "Automations that do the work for you—so you can focus on clients, not software",
        col3: "Great entry-level option, but lacks the horsepower to support growing businesses",
      },
    ],
  },
  "switch-from-booker": {
    pageHeader: {
      heading: "Mangomint vs. Booker",
      subheading: "Here are some reasons why Mangomint is better for growing salons & spas:",
    },
    competitor: {
      name: "Booker",
      logo: BookerLogo,
    },
    rows: [
      {
        col1: "Great for",
        col2: "For salons & spas that want effortless tools and zero hidden fees",
        col3: "For businesses that want lots of features and don’t mind extra costs",
      },
      {
        col1: "Strengths",
        col2: "Sleek, fast, and designed for smooth day-to-day operations—no clunky extras",
        col3: "Diverse feature set, but known for slow load times and surprise fees",
      },
      {
        col1: "Support",
        col2: "U.S.-based support team that actually understands salons and spas. Reponds in under 10 mins",
        col3: "Mixed reviews: support is available but can be slow and inconsistent",
      },
      {
        col1: "Highlight",
        col2: "Advanced automation that cuts costs and boosts profits—without nickel-and-diming you",
        col3: "Offers a ton of features, but expect to pay extra for things like text reminders",
      },
    ],
  },
  "mangomint-med-spa-software-aesthetic-record": {
    pageHeader: {
      heading: "Mangomint vs. Aesthetic Record",
      subheading: "Here are some reasons why Mangomint is better for growing med spas:",
    },
    competitor: {
      name: "Mangomint vs. Aesthetic Record",
      logo: AestheticRecordLogo,
      height: "-5px",
    },
    rows: [
      {
        col1: "Great for",
        col2: "For sophisticated med spas looking to grow",
        col3: "For med spas that need EMR and clinical documentation",
      },
      {
        col1: "Strengths",
        col2: "Lightning-fast, beautifully designed, and optimized for daily operations. Quick and easy to implement for teams of any size.",
        col3: "Built around EMR tools and telehealth—but not ideal for regular med spa workflows",
      },
      {
        col1: "Support",
        col2: "U.S.-based support team that actually understands med spas. Replies in under 10 mins",
        col3: "Email-only support that leaves you waiting...and waiting",
      },
      {
        col1: "Highlight",
        col2: "Powerful automation tools to boost client retention, cut costs, and increase profits—without medical complexity",
        col3: "More complex EMR functionality vs. client experience or day-to-day usability",
      },
    ],
  },
  "mangomint-med-spa-software-aestheticspro": {
    pageHeader: {
      heading: "Mangomint vs. AestheticsPro",
      subheading: "Here are some reasons why Mangomint is better for growing med spas:",
    },
    competitor: {
      name: "Mangomint vs. AestheticsPro",
      logo: AestheticsProLogo,
    },
    rows: [
      {
        col1: "Great for",
        col2: "For modern med spas that want HIPAA compliant next-level tools",
        col3: "For med spas that need EMR and clinical documentation",
      },
      {
        col1: "Strengths",
        col2: "Streamlined design, fast checkouts, and effortless booking—built for med spas",
        col3: "Focuses on medical charting and clinical use, not med spa-friendly UX",
      },
      {
        col1: "Support",
        col2: "U.S.-based humans who understand med spas—reply in 10 mins or less",
        col3: "Support available via phone and email, but less personalized",
      },
      {
        col1: "Highlight",
        col2: "Powerful automation tools to boost client retention, cut costs, and increase profits—without the complexity of legacy software",
        col3: "Better for medical compliance than client experience or day-to-day usability",
      },
    ],
  },
};

export default function LpCompetitorSection({ slug }) {
  useScrolltriggerClassnames();

  const data = pageData[slug];
  if (!data) return null;

  const { pageHeader, competitor, rows } = data;

  return (
    <section className={c.container}>
      <StaticGradient
        top="0%"
        mask="linear-gradient(to bottom, transparent 12.5%, #000, transparent)"
        height="100%"
        opacity="0.45"
      />
      <div className={c.textContainer} data-transition="slide-enter-child">
        <h1 className="h1">{pageHeader.heading}</h1>
        <p className={"p2"}>{pageHeader.subheading}</p>
      </div>

      <div className={c.cardContainer} data-transition="slide-enter">
        <div className={c.row}>
          <div className={classNames(c.col1, c.col)}></div>
          <div className={classNames(c.col2, c.col)}>
            <img className={c.logo} src={MangomintLogo} alt="Mangomint Logo" />
          </div>
          <div className={classNames(c.col3, c.col)}>
            <div className={classNames(c.logo, c.secondaryLogo)}>
              <img
                // Add height modifiers to allow tweaking logo heights for visual consistency
                style={{ "--heightModifier": competitor.height ? competitor.height : "0px" }}
                className={c.secondaryLogoImage}
                src={competitor.logo}
                alt={`${competitor.name} Logo`}
              />
            </div>
          </div>
        </div>

        {rows.map(({ col1, col2, col3 }, i) => (
          <div key={`${competitor.name}-${col1}`} className={c.row}>
            <div className={classNames(c.col1, c.col)}>
              <p className={classNames(c.label, "eyebrow")} dangerouslySetInnerHTML={{ __html: col1 }}></p>
            </div>
            <div className={classNames(c.col2, c.col)}>
              <h2 className={i === 0 ? "h5" : "p3"} dangerouslySetInnerHTML={{ __html: col2 }}></h2>
            </div>
            <div className={classNames(c.col3, c.col)}>
              <h2 className={i === 0 ? "h5" : "p3"} dangerouslySetInnerHTML={{ __html: col3 }}></h2>
            </div>
          </div>
        ))}
      </div>
      <div className={c.arrowContainer} data-transition="addclass">
        <p className={classNames(c.label, "eyebrow")}>
          <span>Find out more</span>
        </p>
        <svg xmlns="http://www.w3.org/2000/svg" width={61} height={65} viewBox="0 0 61 65" fill="none">
          <path
            d="M60 8.09542C50.5352 36.5782 22.8048 38.3479 11.4117 32.2254C-3.49961 22.7699 0.018607 9.58054 7.05553 3.77398C14.0925 -2.03257 28.1438 1.25241 34.8681 13.8577C41.5924 26.4631 40.5408 48.8994 40.5408 63.3052"
            stroke="#474F7B"
            strokeWidth={2}
            strokeLinecap="round"
          />
          <path
            d="M47.5778 52.1406C43.2216 56.8225 41.5461 63.9999 40.7395 63.9999C39.9328 63.9999 35.5145 56.4624 30.4881 53.2211"
            stroke="#474F7B"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </svg>
      </div>
    </section>
  );
}
